// src/pages/agenda/PainelCompromisso.js — painel lateral "Novo compromisso" /
// "Editar" / "Remarcar".
//
// Out/26 (decisão 1 da Agenda): o design só criava pelo Copiloto, mas
// empresa sem chave de IA não usa o Copiloto — o painel é o caminho que
// sempre existe; o Copiloto (fase 3) vira atalho. Mesma linguagem do
// "Configurar teto": painel à direita, tela inteira no celular.
//
// Data e hora vão pro servidor como texto local ("2026-10-20", "15:00"); o
// servidor converte no fuso da operação — o relógio do navegador não entra.
// Conflito de horário AVISA (o responsável já tem algo ali), não impede:
// encaixe é decisão de quem marca.
//
// Out/26 (CRM moderno): o "Lembrar de retornar" da janela do contato abre
// ESTE painel — `inicial.contato` e `inicial.titulo` chegam preenchidos, e
// `camada` põe o painel por cima do modal do contato (o Drawer do MUI fica
// abaixo de um Dialog aberto). Compromisso se grava num lugar só.
import { useEffect, useRef, useState } from 'react';
import { Box, Drawer } from '@mui/material';
import { FiSearch, FiX } from 'react-icons/fi';
import api from '../../services/Api';
import { FONT_NUM, FONT_TXT, botaoBase, rotuloSecao, umTipo } from './estilo';

const TIPOS_PAINEL = ['consulta', 'lembrete', 'tarefa'];
const DURACAO = { consulta: 60, lembrete: 30, tarefa: 60 };
const toque = { '@media (pointer: coarse)': { minHeight: 40 } };

const campo = {
  height: 40, boxSizing: 'border-box', width: '100%', bgcolor: 'var(--ch-inset)', border: '1px solid var(--ch-bdr2)',
  borderRadius: '8px', px: '10px', color: 'var(--ch-text)', fontFamily: FONT_TXT, fontSize: '16px', outline: 'none',
  colorScheme: 'light dark', '&:focus': { borderColor: 'var(--ch-green)' }, '&::placeholder': { color: 'var(--ch-text3)' },
};
const titulo = { ...rotuloSecao, display: 'block', mb: '6px', letterSpacing: '0.14em' };
const pilula = (on) => ({
  ...botaoBase, ...toque, flex: 1, p: '7px 8px', borderRadius: '6px', border: '1px solid', fontSize: '0.72rem', fontWeight: 700,
  color: on ? 'var(--ch-green-d)' : 'var(--ch-text2)', bgcolor: on ? 'var(--ch-green-bg)' : 'transparent',
  borderColor: on ? 'var(--ch-green-b)' : 'var(--ch-bdr)',
});

const somaMin = (hhmm, min) => {
  const [h, m] = hhmm.split(':').map(Number);
  const t = Math.min(23 * 60 + 59, h * 60 + m + min);
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};
const minutos = (hhmm) => { const [h, m] = (hhmm || '0:0').split(':').map(Number); return h * 60 + m; };

function mascarar(fone) {
  const d = String(fone || '').replace(/\D/g, '');
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) return `+55 ${d.slice(2, 4)} ${d[4]}•••• ${d.slice(-4)}`;
  return d.length > 8 ? `+${d.slice(0, 4)}•••• ${d.slice(-4)}` : (d ? `+${d}` : '');
}

export default function PainelCompromisso({ aberto, modo, ev, inicial, config, onFechar, onSalvo, onRemover, camada }) {
  const editando = modo !== 'novo' && !!ev;
  const nome = config?.nome_consulta;
  const [tipo, setTipo] = useState('consulta');
  const [contato, setContato] = useState(null);
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [texto, setTexto] = useState('');
  const [data, setData] = useState('');
  const [ini, setIni] = useState('09:00');
  const [fim, setFim] = useState('10:00');
  const [resp, setResp] = useState('');
  const [status, setStatus] = useState('confirmada');
  const [avisar, setAvisar] = useState(true);
  const [nota, setNota] = useState('');
  const [conflitos, setConflitos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [erro, setErro] = useState('');
  const [confirmarRemocao, setConfirmarRemocao] = useState(false);
  const fechar = useRef(null);
  const dataRef = useRef(null);

  // Abre sempre com o estado certo: o compromisso (editar/remarcar) ou o
  // dia/horário clicado (novo).
  useEffect(() => {
    if (!aberto) return;
    setErro(''); setSalvo(false); setConfirmarRemocao(false); setConflitos([]); setBusca(''); setResultados([]);
    if (editando) {
      setTipo(ev.tipo); setContato(ev.contato); setTexto(ev.tipo === 'consulta' ? '' : ev.titulo);
      setData(ev.data); setIni(ev.ini); setFim(ev.fim); setResp(ev.responsavel?.id || '');
      setStatus(ev.status === 'pendente' ? 'pendente' : 'confirmada'); setNota(ev.nota || '');
      setAvisar(ev.avisar_cliente !== false);
      if (modo === 'remarcar') setTimeout(() => dataRef.current?.focus(), 250);
    } else {
      const t = inicial?.tipo || 'consulta';
      const h0 = inicial?.ini || '09:00';
      setTipo(t); setContato(inicial?.contato || null); setTexto(inicial?.titulo || ''); setData(inicial?.data || config?.hoje || '');
      setIni(h0); setFim(somaMin(h0, DURACAO[t])); setResp(config?.ve_equipe ? '' : config?.eu?.id || '');
      setStatus('confirmada'); setNota(''); setAvisar(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  useEffect(() => () => clearTimeout(fechar.current), []);

  // Busca de contato (a mesma da tela de Contatos — o agente só acha os da
  // carteira dele, como lá).
  useEffect(() => {
    if (!aberto || contato || busca.trim().length < 2) { setResultados([]); return undefined; }
    let vivo = true;
    setBuscando(true);
    const t = setTimeout(() => {
      api.get('/contacts', { params: { q: busca.trim(), per_page: 8 } })
        .then((r) => { if (vivo) setResultados(r.data?.items || []); })
        .catch(() => { if (vivo) setResultados([]); })
        .finally(() => { if (vivo) setBuscando(false); });
    }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca, contato, aberto]);

  // Conflito do responsável no horário — só aviso.
  useEffect(() => {
    if (!aberto || !data || !ini || !fim || minutos(fim) <= minutos(ini)) { setConflitos([]); return undefined; }
    let vivo = true;
    const t = setTimeout(() => {
      api.post('/agenda/conflitos', { data, ini, fim, responsavel_id: resp || undefined, ignorar_id: editando ? ev.id : undefined })
        .then((r) => { if (vivo) setConflitos(r.data?.conflitos || []); })
        .catch(() => { if (vivo) setConflitos([]); });
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
  }, [aberto, data, ini, fim, resp, editando, ev]);

  if (!config) return null;
  const precisaContato = tipo === 'consulta' || tipo === 'lembrete';
  // Fase 2: o lembrete automático ao cliente só existe se a empresa ligou.
  const lembrete = config.lembrete_cliente;
  const mostraAvisar = tipo === 'consulta' && !!lembrete?.ativo;
  const ocupado = salvando || salvo;
  const quemNome = (id) => (config.equipe || []).find((p) => p.id === id)?.nome || (id === config.eu?.id ? 'você' : 'o responsável');
  const respEfetivo = resp || config.eu?.id;

  const trocarTipo = (t) => {
    if (editando) return;
    setTipo(t);
    setFim(somaMin(ini, DURACAO[t]));
    if (t === 'tarefa') setContato(null);
  };
  const trocarIni = (v) => {
    const dur = Math.max(15, minutos(fim) - minutos(ini));
    setIni(v);
    if (v) setFim(somaMin(v, dur));
  };

  const salvar = async () => {
    if (ocupado) return;
    if (precisaContato && !contato) { setErro('Escolha o contato.'); return; }
    if (tipo !== 'consulta' && !texto.trim()) { setErro('Dê um título ao compromisso.'); return; }
    if (!data || !ini || !fim) { setErro('Informe o dia e o horário.'); return; }
    if (minutos(fim) <= minutos(ini)) { setErro('O horário de fim precisa ser depois do início.'); return; }
    setSalvando(true); setErro('');
    const corpo = {
      data, ini, fim, nota,
      ...(precisaContato ? { contato_id: contato.id } : {}),
      ...(tipo !== 'consulta' ? { titulo: texto.trim() } : {}),
      ...(tipo === 'consulta' ? { status } : {}),
      ...(mostraAvisar ? { avisar_cliente: avisar } : {}),
      ...(config.ve_equipe && resp ? { responsavel_id: resp } : {}),
    };
    try {
      const { data: salvoEv } = editando
        ? await api.patch(`/agenda/events/${ev.id}`, corpo)
        : await api.post('/agenda/events', { tipo, ...corpo });
      setSalvo(true);
      onSalvo?.(salvoEv);
      fechar.current = setTimeout(() => onFechar?.(), 700);
    } catch (err) {
      setErro(err?.response?.data?.detail || 'Não foi possível salvar. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async () => {
    if (!confirmarRemocao) { setConfirmarRemocao(true); return; }
    setSalvando(true);
    try { await onRemover?.(ev); onFechar?.(); } catch (err) {
      setErro(err?.response?.data?.detail || 'Não foi possível remover. Tente de novo.');
    } finally { setSalvando(false); }
  };

  const tituloPainel = modo === 'remarcar' ? 'Remarcar' : editando ? 'Editar compromisso' : 'Novo compromisso';
  const rotuloSalvar = salvo ? (editando ? 'Salvo' : 'Marcado') : salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Marcar compromisso';

  return (
    <Drawer anchor="right" open={aberto} onClose={() => !ocupado && onFechar?.()}
      sx={camada ? { zIndex: camada } : undefined}
      PaperProps={{
        'data-screen-label': 'Novo compromisso',
        sx: { width: { xs: '100%', sm: 400 }, boxSizing: 'border-box', bgcolor: 'var(--ch-card)', color: 'var(--ch-text)', fontFamily: FONT_TXT,
          borderLeft: '1px solid var(--ch-bdr2)', boxShadow: '-18px 0 48px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' },
      }}>
      <Box sx={{ p: '14px 20px', borderBottom: '1px solid var(--ch-bdr)', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--ch-text3)', textTransform: 'uppercase' }}>Agenda</Box>
          <Box component="h2" sx={{ m: 0, fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.4 }}>{tituloPainel}</Box>
        </Box>
        <Box component="button" type="button" aria-label="Fechar" onClick={() => !ocupado && onFechar?.()}
          sx={{ ...botaoBase, width: 30, height: 30, '@media (pointer: coarse)': { width: 40, height: 40 }, borderRadius: '7px', border: '1px solid var(--ch-bdr)', bgcolor: 'transparent', color: 'var(--ch-text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0, '&:hover': { color: 'var(--ch-text)', bgcolor: 'var(--ch-inset)' } }}>
          <FiX size={15} />
        </Box>
      </Box>

      {/* Os blocos não encolhem: numa coluna flex que rola, o navegador
          achatava o interruptor e o texto dele vazava por cima do campo de
          baixo (visto em 375px). */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '18px 20px', display: 'flex', flexDirection: 'column', gap: '18px', '& > *': { flexShrink: 0 } }}>
        <Box>
          <Box component="span" sx={titulo}>Tipo</Box>
          <Box role="radiogroup" aria-label="Tipo" sx={{ display: 'flex', gap: '3px', p: '3px', border: '1px solid var(--ch-bdr)', borderRadius: '8px' }}>
            {TIPOS_PAINEL.map((t) => (
              <Box key={t} component="button" type="button" role="radio" aria-checked={tipo === t} disabled={editando && tipo !== t}
                onClick={() => trocarTipo(t)} sx={pilula(tipo === t)}>
                {t === 'consulta' ? umTipo('consulta', nome) : t === 'lembrete' ? 'Lembrete do CRM' : 'Tarefa'}
              </Box>
            ))}
          </Box>
        </Box>

        {precisaContato && (
          <Box>
            <Box component="label" htmlFor="ag-contato" sx={titulo}>Contato</Box>
            {contato ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '8px 10px', borderRadius: '8px', bgcolor: 'var(--ch-inset)', border: '1px solid var(--ch-bdr2)' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ fontSize: '0.82rem', fontWeight: 700, overflowWrap: 'anywhere' }}>{contato.nome}</Box>
                  <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.68rem', color: 'var(--ch-text2)' }}>{contato.fone}</Box>
                </Box>
                <Box component="button" type="button" onClick={() => { setContato(null); setBusca(''); }}
                  sx={{ ...botaoBase, ...toque, border: 'none', bgcolor: 'transparent', color: 'var(--ch-green-d)', fontSize: '0.72rem', fontWeight: 700, p: '6px 4px' }}>
                  Trocar
                </Box>
              </Box>
            ) : (
              <>
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'absolute', left: 10, top: 12, color: 'var(--ch-text3)', display: 'flex' }}><FiSearch size={15} /></Box>
                  <Box component="input" id="ag-contato" value={busca} onChange={(e) => setBusca(e.target.value)} autoComplete="off"
                    placeholder="Buscar por nome ou número" sx={{ ...campo, pl: '32px' }} />
                </Box>
                {(resultados.length > 0 || (busca.trim().length >= 2 && !buscando)) && (
                  <Box role="listbox" aria-label="Contatos encontrados" sx={{ mt: '6px', border: '1px solid var(--ch-bdr)', borderRadius: '8px', overflow: 'hidden' }}>
                    {resultados.length === 0 && (
                      <Box sx={{ p: '10px 12px', fontSize: '0.72rem', color: 'var(--ch-text2)' }}>Nenhum contato com esse nome ou número.</Box>
                    )}
                    {resultados.map((c) => (
                      <Box key={c.id} component="button" type="button" role="option" aria-selected={false}
                        onClick={() => { setContato({ id: c.id, nome: c.name, fone: mascarar(c.phone_number) }); setErro(''); }}
                        sx={{ ...botaoBase, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%', textAlign: 'left', p: '9px 12px', minHeight: 40, border: 'none', borderTop: '1px solid var(--ch-bdr)', '&:first-of-type': { borderTop: 'none' }, bgcolor: 'transparent', color: 'var(--ch-text)', '&:hover': { bgcolor: 'var(--ch-inset)' } }}>
                        <Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{c.name}</Box>
                        <Box component="span" sx={{ fontFamily: FONT_NUM, fontSize: '0.66rem', color: 'var(--ch-text2)', flexShrink: 0 }}>{mascarar(c.phone_number)}</Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {tipo !== 'consulta' && (
          <Box>
            <Box component="label" htmlFor="ag-titulo" sx={titulo}>Título</Box>
            <Box component="input" id="ag-titulo" value={texto} maxLength={160} onChange={(e) => setTexto(e.target.value)}
              placeholder={tipo === 'lembrete' ? 'Ex.: Retornar proposta revisada' : 'Ex.: Reunião de equipe'} sx={campo} />
          </Box>
        )}

        {/* Dia numa linha e horários embaixo: em navegador com relógio de
            12h o campo de hora vira "11:30 AM" e, em três colunas, cortava. */}
        <Box>
          <Box component="label" htmlFor="ag-data" sx={titulo}>Dia</Box>
          <Box component="input" ref={dataRef} id="ag-data" type="date" value={data} onChange={(e) => setData(e.target.value)} sx={{ ...campo, px: '8px', fontFamily: FONT_NUM }} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '8px', mt: '-6px' }}>
          <Box>
            <Box component="label" htmlFor="ag-ini" sx={titulo}>Início</Box>
            <Box component="input" id="ag-ini" type="time" step={300} value={ini} onChange={(e) => trocarIni(e.target.value)} sx={{ ...campo, px: '8px', fontFamily: FONT_NUM }} />
          </Box>
          <Box>
            <Box component="label" htmlFor="ag-fim" sx={titulo}>Fim</Box>
            <Box component="input" id="ag-fim" type="time" step={300} value={fim} onChange={(e) => setFim(e.target.value)} sx={{ ...campo, px: '8px', fontFamily: FONT_NUM }} />
          </Box>
        </Box>

        {conflitos.length > 0 && (
          <Box role="status" sx={{ p: '8px 12px', borderRadius: '8px', bgcolor: 'var(--ch-amber-bg)', border: '1px solid var(--ch-amber-b)', color: 'var(--ch-amber-d)', fontSize: '0.72rem', lineHeight: 1.5 }}>
            {respEfetivo === config.eu?.id ? 'Você já tem' : `${quemNome(respEfetivo)} já tem`} {conflitos.length === 1 ? 'um compromisso' : `${conflitos.length} compromissos`} nesse horário:{' '}
            {conflitos.map((c) => `${c.ini}–${c.fim} ${c.titulo}`).join(' · ')}. Dá pra marcar mesmo assim.
          </Box>
        )}

        {config.ve_equipe ? (
          <Box>
            <Box component="label" htmlFor="ag-resp" sx={titulo}>Responsável</Box>
            <Box component="select" id="ag-resp" value={resp} onChange={(e) => setResp(e.target.value)} sx={{ ...campo, px: '8px' }}>
              <option value="">Eu ({config.eu?.nome})</option>
              {(config.equipe || []).filter((p) => p.id !== config.eu?.id).map((p) => (
                <option key={p.id} value={p.id}>{p.nome}</option>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ fontSize: '0.72rem', color: 'var(--ch-text2)' }}>Responsável: você</Box>
        )}

        {tipo === 'consulta' && (
          <Box>
            <Box component="span" sx={titulo}>Situação</Box>
            <Box role="radiogroup" aria-label="Situação" sx={{ display: 'flex', gap: '3px', p: '3px', border: '1px solid var(--ch-bdr)', borderRadius: '8px' }}>
              {[['confirmada', nome?.genero === 'm' ? 'Confirmado' : 'Confirmada'], ['pendente', 'Pendente']].map(([k, r]) => (
                <Box key={k} component="button" type="button" role="radio" aria-checked={status === k} onClick={() => setStatus(k)} sx={pilula(status === k)}>{r}</Box>
              ))}
            </Box>
          </Box>
        )}

        {mostraAvisar && (
          <Box component="button" type="button" role="switch" aria-checked={avisar} onClick={() => setAvisar((v) => !v)}
            sx={{ ...botaoBase, ...toque, display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%', textAlign: 'left', p: '10px 12px',
              borderRadius: '8px', border: '1px solid var(--ch-bdr)', bgcolor: avisar ? 'var(--ch-green-bg)' : 'transparent', color: 'var(--ch-text)' }}>
            <Box component="span" aria-hidden sx={{ flex: 'none', mt: '1px', width: 30, height: 18, borderRadius: 9, position: 'relative',
              bgcolor: avisar ? 'var(--ch-green)' : 'var(--ch-bdr2)', transition: 'background-color .15s' }}>
              <Box component="span" sx={{ position: 'absolute', top: 2, left: avisar ? 14 : 2, width: 14, height: 14, borderRadius: '50%',
                bgcolor: 'var(--ch-card)', transition: 'left .15s' }} />
            </Box>
            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
              <Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>Avisar o cliente pelo WhatsApp</Box>
              <Box component="span" sx={{ fontSize: '0.68rem', color: 'var(--ch-text2)', lineHeight: 1.45 }}>
                {avisar ? `Lembrete ${(lembrete.antecedencias || []).join(' e ')}.` : 'Sem lembrete automático para este horário.'}
              </Box>
            </Box>
          </Box>
        )}

        <Box>
          <Box component="label" htmlFor="ag-nota" sx={titulo}>Nota</Box>
          <Box component="textarea" id="ag-nota" value={nota} maxLength={2000} rows={3} onChange={(e) => setNota(e.target.value)}
            placeholder="Opcional" sx={{ ...campo, height: 'auto', py: '8px', resize: 'vertical', lineHeight: 1.45 }} />
        </Box>
      </Box>

      {erro && (
        <Box role="alert" sx={{ mx: '20px', mb: '10px', p: '8px 12px', borderRadius: '6px', bgcolor: 'var(--ch-red-bg)', border: '1px solid var(--ch-red-b)', color: 'var(--ch-red-d)', fontSize: '0.72rem', lineHeight: 1.45 }}>
          {erro}
        </Box>
      )}

      <Box sx={{ p: '14px 20px', pb: 'calc(14px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--ch-bdr)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        {editando && onRemover && (
          <Box component="button" type="button" onClick={remover} disabled={ocupado}
            sx={{ ...botaoBase, ...toque, border: 'none', bgcolor: 'transparent', p: '7px 0', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: confirmarRemocao ? 'var(--ch-red)' : 'var(--ch-text2)', '&:hover': { color: 'var(--ch-red)' } }}>
            {confirmarRemocao ? 'Confirmar: remover' : ev.tipo === 'consulta' ? 'Cancelar compromisso' : 'Remover'}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Box component="button" type="button" onClick={() => !ocupado && onFechar?.()}
          sx={{ ...botaoBase, ...toque, fontSize: '0.7rem', fontWeight: 600, color: 'var(--ch-text2)', bgcolor: 'transparent', border: '1px solid transparent', borderRadius: '6px', p: '7px 14px', whiteSpace: 'nowrap', '&:hover': { color: 'var(--ch-text)', bgcolor: 'var(--ch-inset)' } }}>
          Cancelar
        </Box>
        <Box component="button" type="button" onClick={salvar} disabled={ocupado}
          sx={{ ...botaoBase, ...toque, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.02em', color: 'var(--ch-on-green)', bgcolor: 'var(--ch-green)', border: 'none', borderRadius: '6px', p: '7px 16px', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'var(--ch-green-hover)' } }}>
          {rotuloSalvar}
        </Box>
      </Box>
    </Drawer>
  );
}

